import { API_BASE_URL, fetchWithTimeout, handleResponse } from "./shared";

// ============================================================================
// Market Data API
// ============================================================================

export type AssetClass = "future" | "future_option" | "crypto" | "equity";

export interface AssetDto {
  id: string;
  symbol: string;
  displaySymbol: string;
  assetClass: AssetClass;
  exchange: string;
  contractSize: number;
  tickSize: number;
  currency: string;
  metadata: Record<string, unknown> | null;
}

export interface AssetsListResponse {
  assets: AssetDto[];
  count: number;
  scope: "futures" | "crypto" | "all";
  tenantId: string | null;
}

export type Timeframe = "1m" | "5m" | "15m" | "1h" | "4h" | "1d" | "1w" | "1M";

export interface MarketDataCandle {
  time: number;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface TenantCandlesResponse {
  symbol: string;
  interval: Timeframe;
  providerId: string;
  candles: MarketDataCandle[];
}

// --- SFWL-005 Phase 2: market-data providers + BYOD credentials ---

export interface MarketDataProviderInfo {
  id: string;
  displayName: string;
  requiresCredential: boolean;
  capabilities: {
    historicalCandles: boolean;
    realtimeQuotes: boolean;
    realtimeTrades: boolean;
    level2: boolean;
    assetClasses: string[];
  };
}

export interface ProvidersListResponse {
  providers: MarketDataProviderInfo[];
}

export type MarketDataCredentialStatus = "unverified" | "valid" | "invalid";

/** Masked read of a stored org credential. Secrets are reduced to `****abcd`. */
export interface MarketDataCredentialDto {
  id: string;
  providerId: string;
  status: MarketDataCredentialStatus;
  source: "org";
  lastValidatedAt: string | null;
  maskedPayload: Record<string, string>;
  createdAt: string;
  updatedAt: string;
}

export interface CredentialsListResponse {
  credentials: MarketDataCredentialDto[];
}

export interface ValidateCredentialResponse {
  status: MarketDataCredentialStatus;
  error?: string;
}

export const marketDataApi = {
  /**
   * SFWL-004: List the tradeable asset universe for the active tenant.
   * Tenant scoping is done server-side via the x-tenant-id header set by
   * the Next.js middleware.
   */
  async listAssets(): Promise<AssetsListResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/market-data/assets`);
    return handleResponse<AssetsListResponse>(response);
  },

  /**
   * SFWL-005: Tenant-scoped candle fetch via the backend provider registry.
   * Replaces the previous direct browser → Hyperliquid call so BYOD
   * vendor credentials never have to touch the browser.
   *
   * Honors the caller's AbortSignal directly (skips fetchWithTimeout's
   * internal controller so component cleanup actually cancels in-flight
   * requests when market/timeframe changes).
   */
  async getCandles(args: {
    symbol: string;
    interval: Timeframe;
    limit?: number;
    signal?: AbortSignal;
  }): Promise<TenantCandlesResponse> {
    const params = new URLSearchParams({
      symbol: args.symbol,
      interval: args.interval,
    });
    if (args.limit) params.set("limit", String(args.limit));

    const response = await fetch(
      `${API_BASE_URL}/api/market-data/candles?${params.toString()}`,
      { credentials: "include", signal: args.signal }
    );
    return handleResponse<TenantCandlesResponse>(response);
  },

  /**
   * SFWL-005: List registered providers + capabilities. Public (no auth).
   * Drives the MarketDataSettings panel's provider rows.
   */
  async listProviders(): Promise<ProvidersListResponse> {
    const response = await fetchWithTimeout(`${API_BASE_URL}/api/market-data/providers`);
    return handleResponse<ProvidersListResponse>(response);
  },

  /**
   * SFWL-005 Phase 2: list the active org's BYOD credentials (masked).
   * Org-admin scoped; the Sovrun-fallback row is never returned here.
   */
  async listCredentials(): Promise<CredentialsListResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/market-data/credentials`
    );
    return handleResponse<CredentialsListResponse>(response);
  },

  /**
   * Create or replace the org credential for a provider. `payload` is the
   * opaque vendor shape (e.g. CQG `{ username, password, clientAppId }`);
   * it's encrypted at rest and never returned in plaintext.
   */
  async upsertCredential(
    providerId: string,
    payload: Record<string, unknown>
  ): Promise<MarketDataCredentialDto> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/market-data/credentials`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ providerId, payload }),
      }
    );
    return handleResponse<MarketDataCredentialDto>(response);
  },

  /** Run a credential's auth ping and return its new status. */
  async validateCredential(id: string): Promise<ValidateCredentialResponse> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/market-data/credentials/${id}/validate`,
      { method: "POST" }
    );
    return handleResponse<ValidateCredentialResponse>(response);
  },

  /** Delete an org credential (falls the org back to the Sovrun default). */
  async deleteCredential(id: string): Promise<void> {
    const response = await fetchWithTimeout(
      `${API_BASE_URL}/api/market-data/credentials/${id}`,
      { method: "DELETE" }
    );
    if (!response.ok) {
      await handleResponse<void>(response);
    }
  },
};
